import { all, put, call, takeLatest, select } from 'redux-saga/effects';

import {
  employeesRequest,
  employeesSuccess,
  employeesFailure,
  createEmployeeFailure,
  createEmployeeRequest,
  updateEmployeeRequest,
  updateEmployeeFailure,
  deleteEmployeeRequest,
  copyEmployeeRequest,
  copyEmployeeSuccess,
  copyEmployeeFailure,
  deleteEmployeeFailure,
  updateEmployeeFormRequest,
  updateEmployeeFormFailure,
  getUserFailure,
  getUserRequest,
  getUserSuccess,
  deleteEmployeeSuccess,
  updateEmployeeFormSuccess,
  updateEmployeeBadgeSuccess,
  updateEmployeeBadgeFailure,
  updateEmployeeBadgeRequest,
  employeeBadgeRequest,
  employeeBadgeSuccess,
  employeeBadgeFailure,
  employeeSiteEmailSettingsSuccess,
  employeeSiteEmailSettingsFailure,
  employeeSiteEmailSettingsRequest,
  employeeClientEmailSettingsRequest,
  employeeMobilePatrolEmailSettingsRequest,
  employeeMobilePatrolEmailSettingsSuccess,
  employeeMobilePatrolEmailSettingsFailure,
  employeeClientEmailSettingsSuccess,
  employeeClientEmailSettingsFailure,
  updateEmployeeClientEmailSettingsRequest,
  updateEmployeeClientEmailSettingsSuccess,
  updateEmployeeClientEmailSettingsFailure,
  updateEmployeeSiteEmailSettingsSuccess,
  updateEmployeeSiteEmailSettingsFailure,
  updateEmployeeSiteEmailSettingsRequest,
  updateEmployeeMobilePatrolEmailSettingsSuccess,
  updateEmployeeMobilePatrolEmailSettingsFailure,
  updateEmployeeMobilePatrolEmailSettingsRequest,
  createEmployeeSuccess,
  employeesOptionsSuccess,
  employeesOptionsFailure,
  employeesOptionsRequest,
} from './actions';
import { updateLoginDetailsSaga, createLoginDetailsSaga } from '../users/sagas';
import axios from 'axios';
import { EMPLOYEES, EMPLOYEES_V2 } from 'app/api-routes';
import { uploadMediaAssets, mediaAssets } from 'app/api-routes';
import { MediaAssetTables, MediaAssetTypes } from 'ducks/mediaAssets/types';
import { NEW, TABLE_NAMES } from 'app/types/constants';
import { employeeInfoSaga } from 'ducks/employeeInfo/sagas';
import { isLoginDetailsChanged } from 'new-design/helpers';
import _ from 'lodash';
import { updateProfileFieldResultsSaga } from 'ducks/profiles/sagas';

function* employeesSaga({ payload: { siteID } = {} }) {
  try {
    const { payload, warnings } = yield axios.get(EMPLOYEES_V2, { params: { siteID: siteID || null } });

    yield put(employeesSuccess(payload, { warnings }));
  } catch (err) {
    yield put(employeesFailure(err));
  }
}

function* employeesOptionsSaga({ payload: { siteID } = {} }) {
  try {
    const { payload, warnings } = yield axios.get(`${EMPLOYEES_V2}/options`, {
      params: { siteID: siteID || null },
    });

    yield put(employeesOptionsSuccess(payload, { warnings }));
  } catch (err) {
    yield put(employeesOptionsFailure(err));
  }
}

function* createEmployeeSaga({ payload: values, meta }) {
  try {
    const { payload: employeeID } = yield axios.post(EMPLOYEES_V2, { payload: values });

    yield put(createEmployeeSuccess(employeeID));
    meta?.onSuccess?.(employeeID);
  } catch (err) {
    yield put(createEmployeeFailure(err));
    meta?.onFailure?.(err);
  } finally {
    meta?.onFinally?.();
  }
}

function* updateEmployeeSaga({ payload: { employee, callback } }) {
  try {
    yield axios.put(`${EMPLOYEES}/${employee.employeeID}`, { payload: employee });

    if (callback) {
      callback();
    }
  } catch (err) {
    yield put(updateEmployeeFailure(err));
  }
}

function* copyEmployeeSaga({ payload: { employeeID, firstName, lastName, loginName, options }, meta }) {
  try {
    const { payload } = yield axios.post(`${EMPLOYEES}/${employeeID}/copy`, {
      payload: {
        firstName,
        lastName,
        loginName,
        info: !!options?.info,
        address: !!options?.address,
        employeeProfile: !!options?.employeeProfile,
        loginDetails: !!options?.loginDetails,
        sites: !!options?.sites,
        reports: !!options?.reports,
      },
    });
    yield put(copyEmployeeSuccess(payload));
    meta?.onSuccess?.(payload);
  } catch (err) {
    yield put(copyEmployeeFailure(err));
    meta?.onFailure?.(err);
  } finally {
    meta?.onFinally?.();
  }
}

function* deleteEmployeeSaga({ payload: { employeeID, callback, onFailure } }) {
  try {
    yield axios.delete(`${EMPLOYEES}/${employeeID}`);
    yield call(employeesSaga, {});

    if (callback) {
      callback();
    }
    yield put(deleteEmployeeSuccess(employeeID));
  } catch (err) {
    if (onFailure) {
      onFailure();
    }
    yield put(deleteEmployeeFailure(err));
  }
}

function* updateEmployeeFormSubmit({ payload: { id, values, callback } }) {
  try {
    let userID = values?.loginDetails?.userID;
    if (id !== NEW && values.loginDetails.loginName && values.loginDetails.roleID) {
      if (values?.loginDetails?.userID) {
        const loginDetails = yield select((state) => state.users.loginDetails[values.loginDetails.userID]);
        if (isLoginDetailsChanged({ prev: loginDetails, updated: values.loginDetails })) {
          yield call(updateLoginDetailsSaga, {
            payload: {
              userID: values.loginDetails.userID,
              payload: {
                ..._.omit(values.loginDetails, 'siteIDs'),
                userSites: {
                  userSite: values.loginDetails?.siteIDs?.map((siteID) => ({ siteID: siteID })),
                },
              },
            },
          });
        }
      } else {
        userID = yield call(createLoginDetailsSaga, {
          payload: {
            payload: {
              ..._.omit(values.loginDetails, 'siteIDs'),
              userSites: {
                userSite: values.loginDetails?.siteIDs?.map((siteID) => ({ siteID: siteID })),
              },
            },
          },
        });
      }
    }
    const { main, profile } = values;
    if (id !== NEW) {
      if (values.deleteImage !== null) {
        yield axios.delete(`${mediaAssets}/${values.deleteImage}`);
      }
      if (values.uploadedImage) {
        const formData = new FormData();
        formData.append('file', values.uploadedImage);

        const { payload: mediaAssetID } = yield axios.post(uploadMediaAssets, formData, {
          params: {
            entityID: id,
            type: MediaAssetTypes.PHOTO,
            tableName: MediaAssetTables.EMPLOYEES,
          },
        });
        main.photoID = mediaAssetID;
      }
    }

    if (id !== NEW && !_.isEmpty(profile)) {
      yield call(updateProfileFieldResultsSaga, {
        payload: { entityID: id, tableName: TABLE_NAMES.EMPLOYEES, values: profile },
      });
    }

    const body = {
      ...main,
      userID,
      ...(id === NEW ? {} : { employeeID: +id }),
      employeeNotes: {
        notes: Object.values(main.notes || {}),
      },
    };
    delete body.notes;

    if (id === NEW) {
      yield call(createEmployeeSaga, {
        payload: {
          employee: body,
          callback,
        },
      });
    } else {
      yield call(updateEmployeeSaga, {
        payload: {
          employee: body,
          callback,
        },
      });
    }
    // TODO: hot fix, update once logic will be updated. For refresh init state.
    yield call(employeeInfoSaga, { payload: id });
    yield put(updateEmployeeFormSuccess(id));
  } catch (err) {
    yield put(updateEmployeeFormFailure(err));
  }
}

export function* getUserSaga({ payload: { userID, callback } }) {
  try {
    const { payload } = yield axios.get(`${EMPLOYEES}/user/${userID}`);
    if (callback) {
      callback();
    }
    yield put(getUserSuccess(payload));
    return payload;
  } catch (err) {
    yield put(getUserFailure(err));
  }
}

function* updateEmployeeBadgeSaga({ payload: { employeeID, type }, meta }) {
  try {
    const { payload } = yield axios.put(`${EMPLOYEES}/${employeeID}/tag`, { payload: { type } });

    yield put(updateEmployeeBadgeSuccess({ employeeID, payload }));
  } catch (err) {
    yield put(updateEmployeeBadgeFailure(err));
  } finally {
    meta?.onFinally?.();
  }
}

function* employeeBadgeSaga({ payload: employeeID }) {
  try {
    const { payload } = yield axios.get(`${EMPLOYEES}/${employeeID}/tag`);

    yield put(employeeBadgeSuccess(payload));
  } catch (err) {
    yield put(employeeBadgeFailure(err));
  }
}

function* employeeClientEmailSettingsSaga({ payload: employeeID }) {
  try {
    const { payload } = yield axios.get(`${EMPLOYEES_V2}/${employeeID}/client-email-settings`);

    yield put(employeeClientEmailSettingsSuccess({ employeeID, payload }));
  } catch (err) {
    yield put(employeeClientEmailSettingsFailure(err));
  }
}

function* employeeSiteEmailSettingsSaga({ payload: employeeID }) {
  try {
    const { payload } = yield axios.get(`${EMPLOYEES_V2}/${employeeID}/site-email-settings`);

    yield put(employeeSiteEmailSettingsSuccess({ employeeID, payload }));
  } catch (err) {
    yield put(employeeSiteEmailSettingsFailure(err));
  }
}

function* employeeMobilePatrolEmailSettingsSaga({ payload: employeeID }) {
  try {
    const { payload } = yield axios.get(`${EMPLOYEES_V2}/${employeeID}/mobile-patrol-email-settings`);

    yield put(employeeMobilePatrolEmailSettingsSuccess({ employeeID, payload }));
  } catch (err) {
    yield put(employeeMobilePatrolEmailSettingsFailure(err));
  }
}

function* updateEmployeeClientEmailSettingsSaga({ payload: { employeeID, type, clientIDs }, meta }) {
  try {
    const { payload } = yield axios.put(
      `${EMPLOYEES_V2}/${employeeID}/client-email-settings`,
      { payload: clientIDs },
      { params: { type } }
    );

    yield put(updateEmployeeClientEmailSettingsSuccess({ employeeID, type, payload }));
    meta?.onSuccess?.();
  } catch (err) {
    yield put(updateEmployeeClientEmailSettingsFailure(err));
    meta?.onFailure?.();
  } finally {
    meta?.onFinally?.();
  }
}

function* updateEmployeeSiteEmailSettingsSaga({ payload: { employeeID, type, siteIDs }, meta }) {
  try {
    const { payload } = yield axios.put(
      `${EMPLOYEES_V2}/${employeeID}/site-email-settings`,
      { payload: siteIDs },
      { params: { type } }
    );

    yield put(updateEmployeeSiteEmailSettingsSuccess({ employeeID, type, payload }));
    meta?.onSuccess?.();
  } catch (err) {
    meta?.onFailure?.();
    yield put(updateEmployeeSiteEmailSettingsFailure(err));
  } finally {
    meta?.onFinally?.();
  }
}

function* updateEmployeeMobilePatrolEmailSettingsSaga({
  payload: { employeeID, type, mobilePatrolIDs },
  meta,
}) {
  try {
    const { payload } = yield axios.put(
      `${EMPLOYEES_V2}/${employeeID}/mobile-patrol-email-settings`,
      { payload: mobilePatrolIDs },
      { params: { type } }
    );

    yield put(updateEmployeeMobilePatrolEmailSettingsSuccess({ employeeID, type, payload }));
    meta?.onSuccess?.();
  } catch (err) {
    yield put(updateEmployeeMobilePatrolEmailSettingsFailure(err));
    meta?.onFailure?.();
  } finally {
    meta?.onFinally?.();
  }
}

export default function* () {
  yield all([
    yield takeLatest(employeesRequest, employeesSaga),
    yield takeLatest(employeesOptionsRequest, employeesOptionsSaga),
    yield takeLatest(createEmployeeRequest, createEmployeeSaga),
    yield takeLatest(updateEmployeeRequest, updateEmployeeSaga),
    yield takeLatest(deleteEmployeeRequest, deleteEmployeeSaga),
    yield takeLatest(copyEmployeeRequest, copyEmployeeSaga),
    yield takeLatest(updateEmployeeFormRequest, updateEmployeeFormSubmit),
    yield takeLatest(getUserRequest, getUserSaga),
    yield takeLatest(updateEmployeeBadgeRequest, updateEmployeeBadgeSaga),
    yield takeLatest(employeeBadgeRequest, employeeBadgeSaga),
    yield takeLatest(employeeClientEmailSettingsRequest, employeeClientEmailSettingsSaga),
    yield takeLatest(employeeSiteEmailSettingsRequest, employeeSiteEmailSettingsSaga),
    yield takeLatest(employeeMobilePatrolEmailSettingsRequest, employeeMobilePatrolEmailSettingsSaga),
    yield takeLatest(updateEmployeeClientEmailSettingsRequest, updateEmployeeClientEmailSettingsSaga),
    yield takeLatest(updateEmployeeSiteEmailSettingsRequest, updateEmployeeSiteEmailSettingsSaga),
    yield takeLatest(
      updateEmployeeMobilePatrolEmailSettingsRequest,
      updateEmployeeMobilePatrolEmailSettingsSaga
    ),
  ]);
}
